import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { isOverdue } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ListChecks } from "lucide-react";
import ChecklistItemRow from "@/components/task/ChecklistItemRow";

const CHECKLIST_TPLS = [{ key: "cc", label: "CC" }, { key: "da", label: "DA" }, { key: "cdc_cc", label: "CDC/CC" }];

export default function TaskChecklist({ task, checklist, setChecklist, canManage, isAssignee, currentUser, members, memberMap, onSaved, refreshActivity }) {
  const [newItem, setNewItem] = useState("");
  const doneCount = checklist.filter((c) => c.done).length;
  const canTick = canManage || isAssignee;
  const itemOverdue = (it) => isOverdue({ end_date: it.due_date, status: it.done ? "completed" : "open" });

  const applyChecklist = async (key) => {
    try {
      const res = await api.post(`/tasks/${task.task_id}/checklist/apply`, { template_key: key });
      setChecklist(res.data.checklist || []);
      toast.success("Checklist added");
      onSaved();
    } catch (e) { toast.error("Failed to apply checklist"); }
  };
  const addChecklistItem = async () => {
    if (!newItem.trim()) return;
    try {
      const res = await api.post(`/tasks/${task.task_id}/checklist`, { text: newItem });
      setChecklist((c) => [...c, res.data]);
      setNewItem("");
      onSaved();
    } catch (e) { toast.error("Failed to add item"); }
  };
  const toggleItem = async (it) => {
    try {
      const res = await api.patch(`/tasks/${task.task_id}/checklist/${it.item_id}`, { done: !it.done });
      setChecklist(res.data.checklist || []);
      onSaved();
      refreshActivity();
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };
  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/tasks/${task.task_id}/checklist/${itemId}`);
      setChecklist((c) => c.filter((x) => x.item_id !== itemId));
      onSaved();
    } catch (e) { toast.error("Failed to delete"); }
  };
  const assignItem = async (itemId, assignee) => {
    try {
      const res = await api.patch(`/tasks/${task.task_id}/checklist/${itemId}`,
        assignee ? { assignee } : { clear_assignee: true });
      setChecklist(res.data.checklist || []);
      onSaved();
    } catch (e) { toast.error("Failed to assign"); }
  };
  const setItemDue = async (itemId, due) => {
    try {
      const res = await api.patch(`/tasks/${task.task_id}/checklist/${itemId}`,
        due ? { due_date: due } : { clear_due_date: true });
      setChecklist(res.data.checklist || []);
      onSaved();
    } catch (e) { toast.error("Failed to set due date"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
          <ListChecks className="h-3.5 w-3.5" /> Checklist {checklist.length > 0 && <span className="text-zinc-400 normal-case tracking-normal">{doneCount} of {checklist.length}</span>}
        </label>
        {canManage && (
          <div className="flex items-center gap-1.5" data-testid="checklist-templates">
            <span className="text-[10px] uppercase tracking-wider text-zinc-400">1-click:</span>
            {CHECKLIST_TPLS.map((t) => (
              <button key={t.key} type="button" onClick={() => applyChecklist(t.key)}
                data-testid={`apply-checklist-${t.key}`}
                className="text-[11px] font-semibold px-2 py-0.5 rounded-sm border border-zinc-300 text-zinc-700 hover:bg-zinc-900 hover:text-white transition-colors">
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {checklist.length > 0 && (
        <div className="h-1 bg-zinc-100 rounded-full mb-2 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(doneCount / checklist.length) * 100}%` }} />
        </div>
      )}
      <div className="space-y-1 max-h-56 overflow-y-auto">
        {checklist.map((it) => (
          <ChecklistItemRow
            key={it.item_id} it={it} canManage={canManage} canTick={canTick}
            currentUser={currentUser} memberMap={memberMap} members={members} itemOverdue={itemOverdue}
            toggleItem={toggleItem} setItemDue={setItemDue} assignItem={assignItem} deleteItem={deleteItem}
          />
        ))}
      </div>
      {canManage && (
        <div className="flex gap-2 mt-2">
          <Input data-testid="checklist-add-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChecklistItem(); } }}
            placeholder="Add checklist item…" className="rounded-sm h-8 text-sm" />
          <Button type="button" onClick={addChecklistItem} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm h-8 px-3 text-xs">Add</Button>
        </div>
      )}
    </div>
  );
}
